/**
 * Shipments API Transformers
 * Pure field mapping functions - no business logic
 */

import {
  normalizeEntity,
  normalizeRelation,
  toDateOnly,
  parseNumber,
  isRecord,
} from '@ufc/core'

import type {
  Shipment,
  ShipmentStatus,
  ShipmentStats,
  ShipmentsFilter,
  ShipmentFactory,
} from '../domain/types'

import {
  DEFAULT_LOCATION_NAME,
  API_SHIPMENT_STATUS_TO_DOMAIN,
  DOMAIN_SHIPMENT_STATUS_TO_API,
  SHIPMENT_NUMBER_PREFIX,
} from '../domain/constants'

// ============================================
// Helper Functions
// ============================================

function mapApiStatusToDomain(apiStatus: string): ShipmentStatus {
  const normalized = apiStatus.trim()
  return (API_SHIPMENT_STATUS_TO_DOMAIN[normalized as keyof typeof API_SHIPMENT_STATUS_TO_DOMAIN] ?? 'scheduled') as ShipmentStatus
}

/**
 * Transform weighbridge tickets to weighbridge data
 * Finds the most recent ticket with complete data (both gross and tare weights)
 */
export function transformWeighbridgeTickets(tickets: unknown[]): { 
  ticketNumber?: string
  grossWeight?: number
  tareWeight?: number
  netWeight?: number
  measuredAt?: string
  weighType?: string
  scaleId?: string
} | undefined {
  if (!Array.isArray(tickets) || tickets.length === 0) {
    return undefined
  }

  // Normalize and sort tickets by measuredAt descending (most recent first)
  const sortedTickets = tickets
    .map(normalizeEntity)
    .filter((ticket): ticket is Record<string, unknown> => ticket !== null)
    .sort((a, b) => {
      const dateA = typeof a.measuredAt === 'string' ? new Date(a.measuredAt).getTime() : 0
      const dateB = typeof b.measuredAt === 'string' ? new Date(b.measuredAt).getTime() : 0
      return dateB - dateA // descending
    })

  if (sortedTickets.length === 0) {
    return undefined
  }

  // Find first ticket with both gross and tare (complete weighing session)
  const completeTicket = sortedTickets.find(ticket => {
    const hasGross = ticket.grossWeight !== null && ticket.grossWeight !== undefined
    const hasTare = ticket.tareWeight !== null && ticket.tareWeight !== undefined
    return hasGross && hasTare
  })

  // If no complete ticket, use the most recent ticket
  const selectedTicket = completeTicket || sortedTickets[0]

  return {
    ticketNumber: typeof selectedTicket.ticketNumber === 'string' ? selectedTicket.ticketNumber : undefined,
    grossWeight: parseNumber(selectedTicket.grossWeight) || undefined,
    tareWeight: parseNumber(selectedTicket.tareWeight) || undefined,
    netWeight: parseNumber(selectedTicket.netWeight) || undefined,
    measuredAt: typeof selectedTicket.measuredAt === 'string' ? selectedTicket.measuredAt : undefined,
    weighType: typeof selectedTicket.weighType === 'string' ? selectedTicket.weighType : undefined,
    scaleId: typeof selectedTicket.scaleId === 'string' ? selectedTicket.scaleId : undefined,
  }
}

function inferPackaging(variant: Record<string, unknown> | null): 'bags' | 'bulk' {
  if (!variant) return 'bags'
  const haystack = `${variant.skuCode ?? ''} ${variant.name ?? ''}`.toLowerCase()
  if (haystack.includes('bulk') || haystack.includes('ton')) return 'bulk'
  return 'bags'
}

function normalizeLocalizedText(value: unknown): { en: string; ar: string } {
  if (typeof value === 'string') {
    return { en: value, ar: value }
  }
  if (typeof value === 'object' && value !== null) {
    const obj = value as Record<string, unknown>
    return {
      en: typeof obj.en === 'string' ? obj.en : '',
      ar: typeof obj.ar === 'string' ? obj.ar : (typeof obj.en === 'string' ? obj.en : ''),
    }
  }
  return { en: '', ar: '' }
}

function formatLocationName(location: Record<string, unknown> | null): string {
  if (!location) return DEFAULT_LOCATION_NAME
  const name = typeof location.name === 'string' ? location.name.trim() : ''
  if (name) return name
  const addressLine1 = typeof location.addressLine1 === 'string' ? location.addressLine1.trim() : ''
  const addressLine2 = typeof location.addressLine2 === 'string' ? location.addressLine2.trim() : ''
  const postalCode = typeof location.postalCode === 'string' ? location.postalCode.trim() : ''
  const parts = [addressLine1, addressLine2, postalCode].filter(Boolean)
  return parts.join(', ') || DEFAULT_LOCATION_NAME
}

function generateShipmentNumber(entity: Record<string, unknown>): string {
  const id = entity.id
  const base = id != null ? String(id) : ''
  return `${SHIPMENT_NUMBER_PREFIX}-${base.toUpperCase()}`
}

function transformFactoryRelation(factory: Record<string, unknown> | null): ShipmentFactory | undefined {
  if (!factory) return undefined
  const id = factory.id ?? factory.documentId
  const documentId = factory.documentId ?? factory.id
  const code = factory.code
  const name = factory.name

  if (id == null || code == null || name == null) return undefined

  return {
    id: String(id),
    documentId: String(documentId),
    code: String(code),
    name: String(name),
  }
}

// ============================================
// Shipment Transformer
// ============================================

export interface ApiShipmentEntity {
  id?: number
  documentId?: string
  shipmentNumber?: string
  order?: unknown
  origin?: unknown
  destination?: unknown
  resource?: unknown
  tenant?: unknown
  factory?: unknown
  shipmentStatus?: string
  dispatchedAt?: string
  arrivedAt?: string
  metadata?: unknown
  createdAt?: string
  updatedAt?: string
}

export function transformShipmentResponseToDomain(raw: unknown): Shipment {
  const entity = normalizeEntity<ApiShipmentEntity>(raw)
  if (!entity) {
    throw new Error('Invalid shipment entity')
  }

  const order = normalizeRelation<Record<string, unknown>>(entity.order)
  const variant = normalizeRelation<Record<string, unknown>>(order?.variant)
  const sku = normalizeRelation<Record<string, unknown>>(variant?.sku)

  const dropoff = normalizeRelation<Record<string, unknown>>(order?.dropoff)
  const origin = normalizeRelation<Record<string, unknown>>(entity.origin)
  const destination = normalizeRelation<Record<string, unknown>>(entity.destination) || dropoff

  const resource = normalizeRelation<Record<string, unknown>>(entity.resource)
  const driver = normalizeRelation<Record<string, unknown>>(resource?.driver)
  const powerUnit = normalizeRelation<Record<string, unknown>>(resource?.powerUnit)
  const tenant = normalizeRelation<Record<string, unknown>>(entity.tenant)
  const factory = normalizeRelation<Record<string, unknown>>(entity.factory)

  const pricingSnapshot = isRecord(order?.pricingSnapshot) ? order.pricingSnapshot : {}

  // Order number from pricing snapshot
  const rawOrderNumber = pricingSnapshot.orderNumber
  const normalizedOrderNumber =
    typeof rawOrderNumber === 'string'
      ? rawOrderNumber.trim()
      : rawOrderNumber != null
        ? String(rawOrderNumber).trim()
        : ''

  const orderId = String(order?.documentId ?? order?.id ?? '')
  const orderNumberFallbackBase = order?.id != null ? String(order.id) : orderId
  const orderNumberFallback = orderNumberFallbackBase
    ? `ORD-${orderNumberFallbackBase.slice(0, 8).toUpperCase()}`
    : 'ORD'

  // Product name
  const productName = pricingSnapshot.productName ?? sku?.name ?? variant?.name
  const productLocalized = normalizeLocalizedText(productName)

  // Packaging and unit
  const packaging = String(pricingSnapshot.packaging ?? inferPackaging(variant))
  const unit = packaging === 'bulk' ? 'tons' : 'bags'

  // Quantity
  const quantityValue = parseNumber(order?.quantity)

  // Metadata
  const metadata = isRecord(entity.metadata) ? entity.metadata : undefined
  const eta = typeof metadata?.eta === 'string' ? metadata.eta : undefined

  // Weighbridge data - NOT loaded here (loaded separately via getShipmentWeighbridgeTickets)
  // This keeps the main shipment list/detail queries lightweight

  // Destination address fallback
  const orderMetadata = isRecord(order?.metadata) ? order.metadata : {}
  const destinationAddress = formatLocationName(destination)
  const fallbackAddress = typeof orderMetadata.deliveryAddress === 'string'
    ? orderMetadata.deliveryAddress.trim()
    : ''

  return {
    id: String(entity.documentId ?? entity.id ?? ''),
    numericId: typeof entity.id === 'number' ? entity.id : undefined,
    shipmentNumber: String(entity.shipmentNumber || '').trim() || generateShipmentNumber(entity as Record<string, unknown>),
    orderId,
    orderNumber: normalizedOrderNumber && normalizedOrderNumber !== '0' ? normalizedOrderNumber : orderNumberFallback,
    product: productLocalized,
    quantity: { value: quantityValue, unit },
    status: mapApiStatusToDomain(String(entity.shipmentStatus ?? 'pending')),
    scheduledDate: toDateOnly(entity.dispatchedAt ?? order?.deliveryDate ?? entity.createdAt),
    deliveryDate: toDateOnly(entity.arrivedAt) || undefined,
    origin: formatLocationName(origin),
    destination: destinationAddress !== DEFAULT_LOCATION_NAME ? destinationAddress : (fallbackAddress || DEFAULT_LOCATION_NAME),
    driverName: typeof driver?.name === 'string' ? driver.name : undefined,
    truckNumber:
      typeof powerUnit?.licensePlate === 'string'
        ? powerUnit.licensePlate
        : typeof powerUnit?.assetNumber === 'string'
          ? powerUnit.assetNumber
          : undefined,
    // weighbridge: undefined - loaded separately via getShipmentWeighbridgeTickets
    eta,
    factory: transformFactoryRelation(factory),
    tenant: tenant?.name && tenant?.code
      ? { name: String(tenant.name), code: String(tenant.code) }
      : undefined,
    metadata,
    createdAt: toDateOnly(entity.createdAt),
    updatedAt: toDateOnly(entity.updatedAt ?? entity.createdAt),
  }
}

export function transformShipmentsResponseToDomain(raw: unknown[]): Shipment[] {
  return raw.map(transformShipmentResponseToDomain)
}

// ============================================
// Stats Transformer
// ============================================

export function computeStats(shipments: Shipment[]): ShipmentStats {
  return {
    scheduled: shipments.filter(s => s.status === 'scheduled').length,
    loading: shipments.filter(s => s.status === 'loading').length,
    delivered: shipments.filter(s => s.status === 'delivered').length,
    total: shipments.length,
  }
}

// ============================================
// Filter Transformer
// ============================================

export function transformFilterToParams(filter?: ShipmentsFilter): URLSearchParams {
  const params = new URLSearchParams()

  if (filter?.status && filter.status !== 'all') {
    const apiStatus = DOMAIN_SHIPMENT_STATUS_TO_API[filter.status as keyof typeof DOMAIN_SHIPMENT_STATUS_TO_API]
    if (filter.status === 'delivered') {
      params.set('filters[shipmentStatus][$in][0]', 'arrived')
      params.set('filters[shipmentStatus][$in][1]', 'completed')
    } else if (apiStatus) {
      params.set('filters[shipmentStatus][$eq]', apiStatus)
    }
  }

  return params
}
